import { useCallback } from 'react'
import { useQueries, type UseQueryResult } from '@tanstack/react-query'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { journeyRequests, type JourneySubject } from '@/lib/journeyCacheKey'
import { PROCEDURE_JOURNEY_KEY } from '@/lib/queryClient'
import { useTenant } from '@/providers/TenantProvider'
import type { ProcedureJourney } from '@/types/cgt'

/**
 * The query key for one list-progress journey. Exported so the case page can
 * mark its own procedure's row stale when the user leaves it.
 */
export function procedureJourneyQueryKey(tenantId: string | null, cacheKey: string) {
  return [...PROCEDURE_JOURNEY_KEY, tenantId, cacheKey] as const
}

/**
 * How long a list-progress journey is served from cache with no write in
 * between. Long enough to cover list → case → back; short enough that another
 * user's progress shows up on the next visit.
 */
const JOURNEY_STALE_MS = 30_000

/**
 * Progress journeys for the procedure rows on screen, keyed by
 * `journeyCacheKey`.
 *
 * These used to live in the page's component state, which was lost on every
 * navigation: each return to the list re-fired one journey request per row (20
 * by default). The API limits each user per endpoint, and with the case page
 * asking the same endpoint, a few list-case round trips ran it out (z8nrz7e1c2). In the query cache they survive the
 * round trip, and any write marks them stale (`markProcedureJourneysStale`), so
 * a step the user just completed still shows on return.
 *
 * A failed fetch leaves its key out of the map, as `Promise.allSettled` did
 * before; the row renders without progress.
 */
export function useProcedureJourneys(
  procedures: readonly JourneySubject[],
): Map<string, ProcedureJourney> {
  const { selectedTenantId: tenantId } = useTenant()
  const requests = journeyRequests(procedures)

  const combine = useCallback(
    (results: UseQueryResult<ProcedureJourney>[]) => {
      const byKey = new Map<string, ProcedureJourney>()
      results.forEach((r, i) => {
        if (r.data) byKey.set(requests[i].key, r.data)
      })
      return byKey
    },
    // `requests` is rebuilt every render; its keys are what the map depends on.
    [requests.map(r => r.key).join('|')],
  )

  return useQueries({
    queries: requests.map(r => ({
      queryKey: procedureJourneyQueryKey(tenantId, r.key),
      queryFn: () => procedureTemplatesApi.journey(r.procedureTemplateId, r.patientMrn, r.procedureId),
      staleTime: JOURNEY_STALE_MS,
      // One attempt per row, as the Promise.allSettled version made: a retry
      // here doubles the burst on exactly the endpoint that ran out (z8nrz7e1c2).
      retry: false,
    })),
    combine,
  })
}
